// Error Reporting Utility for Frontend Apps
// Shared across Storefront, Admin, CRM

type ErrorSeverity = 'info' | 'warn' | 'error' | 'fatal';

interface ErrorReport {
  app: string;
  env: string;
  severity: ErrorSeverity;
  message: string;
  stack?: string;
  url?: string;
  fingerprint?: string;
  user_id?: string;
  lead_id?: string;
  extra?: Record<string, any>;
}

const QUEUE_KEY = 'error_queue';
const API_ENDPOINT = '/errors';
const BUILD_VERSION = import.meta.env.VITE_BUILD_VERSION || 'dev';

let isInitialized = false;
let appName = 'unknown';
let apiBase = '';

// Generate fingerprint from error
function generateFingerprint(message: string, stack?: string): string {
  const normalized = [
    message?.substring(0, 200) || '',
    stack?.split('\n')[0] || ''
  ].join('|');
  
  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    const char = normalized.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
}

// Get queued errors from localStorage
function getQueue(): ErrorReport[] {
  try {
    const queue = localStorage.getItem(QUEUE_KEY);
    return queue ? JSON.parse(queue) : [];
  } catch {
    return [];
  }
}

// Save queue to localStorage
function saveQueue(queue: ErrorReport[]): void {
  try {
    // Keep max 50 errors in queue
    const trimmed = queue.slice(-50);
    localStorage.setItem(QUEUE_KEY, JSON.stringify(trimmed));
  } catch {
    // localStorage might be full or unavailable
  }
}

// Send error to API
async function sendError(report: ErrorReport): Promise<boolean> {
  try {
    const response = await fetch(`${apiBase}${API_ENDPOINT}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(report),
    });
    return response.ok || response.status === 429; // Consider rate-limited as "sent"
  } catch {
    return false;
  }
}

// Report error
export async function reportError(
  error: Error | string,
  severity: ErrorSeverity = 'error',
  extra?: Record<string, any>
): Promise<void> {
  const message = typeof error === 'string' ? error : error.message;
  const stack = typeof error === 'string' ? undefined : error.stack;
  
  const report: ErrorReport = {
    app: appName,
    env: import.meta.env.MODE === 'production' ? 'prod' : 'dev',
    severity,
    message,
    stack,
    url: window.location.href,
    fingerprint: generateFingerprint(message, stack),
    extra: {
      ...extra,
      build_version: BUILD_VERSION,
      user_agent: navigator.userAgent,
    },
  };
  
  // Try to send immediately
  const sent = await sendError(report);
  
  if (!sent) {
    // Queue for later
    const queue = getQueue();
    queue.push(report);
    saveQueue(queue);
  }
}

// Flush queued errors
export async function flushErrorQueue(): Promise<void> {
  const queue = getQueue();
  if (queue.length === 0) return;
  
  const remaining: ErrorReport[] = [];
  
  for (const report of queue) {
    const sent = await sendError(report);
    if (!sent) {
      remaining.push(report);
    }
  }
  
  saveQueue(remaining);
}

// Initialize error capture
export function initErrorCapture(config: {
  app: 'storefront' | 'admin' | 'crm';
  apiBase: string;
}): void {
  if (isInitialized) return;
  
  appName = config.app;
  apiBase = config.apiBase;
  isInitialized = true;
  
  // Global error handler
  window.onerror = (message, source, lineno, colno, error) => {
    reportError(
      error || String(message),
      'error',
      { source, lineno, colno }
    );
  };
  
  // Unhandled promise rejections
  window.onunhandledrejection = (event) => {
    const message = event.reason?.message || String(event.reason);
    reportError(
      event.reason instanceof Error ? event.reason : message,
      'error',
      { type: 'unhandledrejection' }
    );
  };
  
  // Flush queue on load
  setTimeout(flushErrorQueue, 5000);
  
  // Flush queue periodically
  setInterval(flushErrorQueue, 60000);
  
  // Flush before unload
  window.addEventListener('beforeunload', () => {
    const queue = getQueue();
    if (queue.length > 0) {
      // Use sendBeacon for reliable delivery
      navigator.sendBeacon(
        `${apiBase}${API_ENDPOINT}`,
        JSON.stringify(queue[0]) // Send at least one
      );
    }
  });
  
  // Check for test error trigger
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('throw') === '1') {
    setTimeout(() => {
      throw new Error('Test error triggered via ?throw=1');
    }, 1000);
  }
  
  console.log(`[${config.app}] Error capture initialized`);
}

// Export for ErrorBoundary usage
export function captureException(error: Error, componentStack?: string): void {
  reportError(error, 'fatal', { componentStack });
}
